import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Brain as A1, CheckCircle as A2, Target as A3, BadgeCheck as A4, TrendingUp as A5, Layers as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Brain, Target, Sparkles, TrendingUp, Award, Zap, Star, CheckCircle,
  AlertTriangle, ArrowRight, RefreshCw, BookOpen, Briefcase, Compass, Cpu
} from 'lucide-react';
import { toast } from 'sonner';
import { showFriendlyError } from '../utils/friendlyErrors';
import axios from 'axios';
import { motion } from 'framer-motion';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// Quiz definitions, 10 questions covering tools, tasks, energy, industries,
// monetizable skills, target career, soft/technical skills, AI comfort, env.
// ─────────────────────────────────────────────────────────────────────────────
const QUIZ_QUESTIONS = [
  {
    id: 'tools_used',
    question: 'Which tools have you used professionally?',
    type: 'multi',
    options: [
      'Excel/Google Sheets', 'Slack/Teams', 'JIRA/Asana/Trello', 'Salesforce/HubSpot',
      'Figma/Adobe Creative', 'Power BI/Tableau', 'Python/SQL', 'AI tools (ChatGPT, Claude, Gemini)',
      'Notion/Airtable', 'Zapier/Make/n8n', 'WordPress/Webflow', 'Git/GitHub',
      'Looker/Mode/Metabase', 'Snowflake/BigQuery', 'AWS / GCP / Azure', 'Docker/Kubernetes',
      'Mailchimp/Klaviyo/Marketo', 'Google Analytics/Mixpanel', 'Zendesk/Intercom',
      'Canva/Loom/Descript', 'QuickBooks/Xero/NetSuite', 'Workday/BambooHR',
    ],
  },
  {
    id: 'tasks_others_ask',
    question: 'What tasks do people usually ask you to help with?',
    type: 'multi',
    options: [
      'Organize projects', 'Write/edit content', 'Solve technical problems',
      'Lead meetings', 'Analyze data', 'Design something', 'Coach/train others',
      'Sell/persuade', 'Manage relationships', 'Build processes',
      'Create presentations', 'Mediate conflicts', 'Plan events',
      'Recruit/hire', 'Fundraise / pitch', 'Edit videos / podcasts',
      'Build dashboards', 'Automate workflows', 'Write code', 'Research / synthesize',
    ],
  },
  {
    id: 'work_energy',
    question: 'What type of work gives you energy?',
    type: 'multi',
    options: [
      'Building things from scratch', 'Optimizing existing processes', 'Helping people grow',
      'Solving complex puzzles', 'Telling stories/communicating', 'Leading teams',
      'Working with data', 'Designing experiences', 'Closing deals',
      'Teaching / mentoring', 'Strategic planning', 'Crisis problem-solving',
      'Researching / learning new domains', 'Public speaking / presenting',
    ],
  },
  {
    id: 'industries',
    question: 'What industries have you worked in?',
    type: 'multi',
    options: [
      'Technology/SaaS', 'Healthcare', 'Education', 'Finance/Banking', 'Retail/E-commerce',
      'Manufacturing', 'Government/Nonprofit', 'Media/Marketing', 'Hospitality',
      'Real Estate', 'Consulting', 'Legal', 'Energy/Utilities', 'Transportation/Logistics',
      'Agriculture/Food', 'Insurance', 'Telecom', 'Construction', 'Aerospace/Defense',
      'Sports/Entertainment',
    ],
  },
  {
    id: 'monetize',
    question: 'What skills do you most want to monetize next?',
    type: 'text',
    placeholder: 'e.g., AI workflow design, project leadership, content creation, coaching, B2B sales…',
  },
  {
    id: 'target_career',
    question: 'What career are you trying to pivot into?',
    type: 'text',
    placeholder: 'e.g., Senior Product Manager, AI Operations Specialist, Customer Success Lead, Marketing Director',
  },
  {
    id: 'soft_skills',
    question: 'What are your strongest soft skills?',
    type: 'multi',
    options: [
      'Communication', 'Leadership', 'Empathy', 'Negotiation', 'Public speaking',
      'Conflict resolution', 'Coaching', 'Critical thinking', 'Adaptability',
      'Decision making', 'Time management', 'Storytelling', 'Active listening',
      'Cultural awareness', 'Resilience', 'Strategic thinking', 'Cross-functional collaboration',
      'Influence without authority', 'Mentoring', 'Customer empathy',
    ],
  },
  {
    id: 'tech_skills',
    question: 'What are your strongest technical skills?',
    type: 'multi',
    options: [
      'SQL/Databases', 'Python/Scripting', 'Excel modeling', 'Design tools (Figma/Adobe)',
      'Marketing automation', 'Data visualization', 'CRM systems', 'Web development',
      'No-code/automation (Zapier/Make/Bubble)', 'AI tools / prompt engineering',
      'Cloud (AWS/GCP/Azure)', 'JavaScript/TypeScript', 'React/Vue/Angular',
      'Java/.NET/Go', 'API integrations', 'DevOps/CI-CD',
      'Cybersecurity basics', 'Mobile dev (iOS/Android)', 'Statistics/Modeling',
      'A/B testing', 'SEO/SEM', 'Video editing', 'UX research', 'Financial modeling',
    ],
  },
  {
    id: 'ai_comfort',
    question: 'How comfortable are you with AI tools today?',
    type: 'single',
    options: ['Never used them', 'Tried a few times', 'Use weekly', 'Use daily',
              'I build with AI APIs / agents'],
  },
  {
    id: 'work_env',
    question: 'What work environment do you prefer?',
    type: 'single',
    options: ['Remote async', 'Remote synchronous', 'Hybrid', 'In-office collaborative',
              'Independent / consulting'],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Reusable score visuals
// ─────────────────────────────────────────────────────────────────────────────
function CircularScore({ score, size = 90, label }) {
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color =
    score >= 80 ? '#10b981' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center" data-testid={`circular-score-${label || 'x'}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} stroke="#1e293b" strokeWidth="6" fill="none" />
          <circle
            cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth="6" fill="none"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-white">{score}</span>
        </div>
      </div>
      {label && <span className="text-xs text-slate-300 mt-1 text-center">{label}</span>}
    </div>
  );
}

function SkillBar({ label, score }) {
  const color =
    score >= 80 ? 'from-emerald-500 to-emerald-400' :
    score >= 60 ? 'from-blue-500 to-blue-400' :
    score >= 40 ? 'from-amber-500 to-amber-400' :
    'from-red-500 to-red-400';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-300 capitalize">{label.replace(/_/g, ' ')}</span>
        <span className="text-white font-medium">{score}</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${color}`} style={{ width: `${score}%`, transition: 'width 1s ease-out' }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function SkillsAssessmentPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [step, setStep] = useState('intro'); // intro | quiz | loading | report
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [latest, setLatest] = useState(null);
  const [hasResume, setHasResume] = useState(false);

  useEffect(() => {
    // Try to load most recent assessment
    (async () => {
      try {
        const res = await axios.get(`${API}/skills-assessment/latest`, {
          ...getAuthHeader(), withCredentials: true,
        });
        if (res.data.assessment) {
          setLatest(res.data.assessment);
        }
      } catch (e) { /* No-op */ }
      // Check whether a resume has been uploaded so we can show evidence-grounded scoring
      try {
        const r = await axios.get(`${API}/resume/analyses`, {
          ...getAuthHeader(), withCredentials: true,
        });
        setHasResume(Array.isArray(r.data?.analyses) && r.data.analyses.length > 0);
      } catch (e) { /* No-op */ }
    })();
  }, [getAuthHeader]);

  const handleAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const toggleMulti = (qid, opt) => {
    setAnswers((prev) => {
      const cur = prev[qid] || [];
      return { ...prev, [qid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });
  };

  const advance = () => {
    const q = QUIZ_QUESTIONS[currentQ];
    if (q.type === 'multi') {
      if (!answers[q.id] || answers[q.id].length === 0) {
        toast.error('Please select at least one option'); return;
      }
    } else if (q.type === 'single') {
      if (!answers[q.id]) {
        toast.error('Please choose one option'); return;
      }
    } else {
      if (!answers[q.id] || String(answers[q.id]).trim().length < 3) {
        toast.error('Please type a short answer'); return;
      }
    }
    if (currentQ < QUIZ_QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      submitAnalysis();
    }
  };

  const submitAnalysis = async () => {
    setStep('loading');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/skills-assessment/analyze`,
        { answers, target_career: answers.target_career || '' },
        { ...getAuthHeader(), withCredentials: true }
      );
      if (res.data.success) {
        setReport(res.data.report);
        setStep('report');
        toast.success('Your Skills Assessment is ready!');
      } else {
        throw new Error('Analysis failed');
      }
    } catch (err) {
      showFriendlyError(err, { fallback: "We couldn't complete the assessment. Please try again." });
      setStep('intro');
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep('quiz');
    setCurrentQ(0);
    setAnswers({});
    setReport(null);
  };

  const viewLatest = () => {
    if (latest?.report) {
      setReport(latest.report);
      setStep('report');
    }
  };

  // ─── INTRO ───────────────────────────────────────────────────────────────
  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="skills-assessment-arrival"
        eyebrow="Skills Assessment"
        title="Find out where you actually stand."
        subtitle="A short, honest check of what you can do right now against what your target needs, so the gaps you work on are the real ones."
        accent="#34d399"
        second="#22d3ee"
        ctaLabel="Start the check"
        narration={"This is the Skills Assessment. It is a short, honest check of where you stand right now against what your target actually requires. No trick questions and no score to show off, the output is a list of the gaps that matter, ranked, with the fastest way to close each one."}
        facts={[
          { icon: A2, head: "Short and honest", sub: "No trick questions, no padding" },
          { icon: A3, head: "Against your target", sub: "Measured on what the job needs" },
          { icon: A5, head: "Ranked gaps", sub: "The ones worth closing first" }
        ]}
        modules={[
          { icon: A1, label: "Where you stand", line: "Your current level, by skill area", hue: "#34d399" },
          { icon: A3, label: "What the job needs", line: "Requirements for your target role", hue: "#22d3ee" },
          { icon: A6, label: "The gap", line: "Difference between the two, made specific", hue: "#fbbf24" },
          { icon: A5, label: "Priority order", line: "Which gap costs you the most right now", hue: "#a78bfa" },
          { icon: A4, label: "Proof level", line: "How well each skill is evidenced", hue: "#f472b6" },
          { icon: A2, label: "Next step", line: "One action per gap, all of them doable", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  if (step === 'intro') {
    return (
      <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="skills" /></div>
        <PageHeader
          title="Skills Assessment"
          description="Discover your strongest skills, uncover hidden career assets, and identify the fastest path to your next opportunity."
        />

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <GlassCard className="text-center">
            <Brain className="w-10 h-10 mx-auto mb-3 text-blue-400" />
            <h3 className="text-white font-semibold mb-1">12 Skill Categories</h3>
            <p className="text-sm text-slate-300">Leadership, AI, data, sales, ops & more, scored 0-100.</p>
          </GlassCard>
          <GlassCard className="text-center">
            <Compass className="w-10 h-10 mx-auto mb-3 text-violet-400" />
            <h3 className="text-white font-semibold mb-1">Career Pivot Matches</h3>
            <p className="text-sm text-slate-300">AI-ranked roles you could pivot into based on real signals.</p>
          </GlassCard>
          <GlassCard className="text-center">
            <Cpu className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
            <h3 className="text-white font-semibold mb-1">AI Readiness Score</h3>
            <p className="text-sm text-slate-300">How prepared you are for the AI-powered future of work.</p>
          </GlassCard>
        </div>

        <GlassCard className="bg-gradient-to-br from-blue-600/10 to-violet-600/10 border-blue-500/30">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Take the 10-question quiz</h2>
              <p className="text-slate-300">Takes about 3 minutes. You'll get a personalised career intelligence report at the end.</p>
              {hasResume ? (
                <p className="text-emerald-400 text-sm mt-2 flex items-center gap-2" data-testid="resume-detected-banner">
                  <CheckCircle className="w-4 h-4" /> Your uploaded resume will be analyzed alongside your quiz answers
                </p>
              ) : (
                <p className="text-amber-400 text-sm mt-2 flex items-center gap-2" data-testid="no-resume-banner">
                  <AlertTriangle className="w-4 h-4" />
                  Tip: <a href="/app/resume-ats" className="underline hover:text-amber-300">upload your resume first</a> for evidence-grounded scoring
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {latest && (
                <Button onClick={viewLatest} variant="outline" className="border-slate-700" data-testid="view-latest-btn">
                  View Last Report
                </Button>
              )}
              <Button onClick={() => setStep('quiz')} className="bg-blue-600 hover:bg-blue-700" data-testid="start-quiz-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Start Assessment
              </Button>
            </div>
          </div>
        </GlassCard>
      </AppLayout>
    );
  }

  // ─── QUIZ ───────────────────────────────────────────────────────────────
  if (step === 'quiz') {
    const q = QUIZ_QUESTIONS[currentQ];
    const progress = ((currentQ + 1) / QUIZ_QUESTIONS.length) * 100;
    return (
      <AppLayout>
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-slate-300 mb-2">
              <span>Question {currentQ + 1} of {QUIZ_QUESTIONS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500" style={{ width: `${progress}%`, transition: 'width 0.3s' }} />
            </div>
          </div>

          <GlassCard data-testid={`quiz-question-${q.id}`}>
            <h2 className="text-xl font-bold text-white mb-6">{q.question}</h2>

            {q.type === 'multi' && (
              <div className="grid md:grid-cols-2 gap-2 mb-6">
                {q.options.map((opt) => {
                  const checked = (answers[q.id] || []).includes(opt);
                  return (
                    <button
                      key={opt}
                      onClick={() => toggleMulti(q.id, opt)}
                      className={`text-left px-4 py-3 rounded-lg border transition-all ${
                        checked
                          ? 'bg-blue-500/20 border-blue-500 text-white'
                          : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500'
                      }`}
                      data-testid={`quiz-opt-${q.id}-${opt.replace(/[^a-z0-9]/gi, '_')}`}
                    >
                      <span className="text-sm">{opt}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {q.type === 'single' && (
              <div className="space-y-2 mb-6">
                {q.options.map((opt) => {
                  const checked = answers[q.id] === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => handleAnswer(q.id, opt)}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                        checked
                          ? 'bg-blue-500/20 border-blue-500 text-white'
                          : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500'
                      }`}
                      data-testid={`quiz-opt-${q.id}-${opt.replace(/[^a-z0-9]/gi, '_')}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}

            {q.type === 'text' && (
              <Textarea
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswer(q.id, e.target.value)}
                placeholder={q.placeholder}
                className="bg-slate-900/40 border-slate-700 mb-6"
                rows={3}
                data-testid={`quiz-text-${q.id}`}
              />
            )}

            <div className="flex justify-between">
              <Button
                onClick={() => currentQ > 0 ? setCurrentQ(currentQ - 1) : setStep('intro')}
                variant="outline"
                className="border-slate-700"
                data-testid="quiz-back"
              >
                Back
              </Button>
              <Button onClick={advance} className="bg-blue-600 hover:bg-blue-700" data-testid="quiz-next">
                {currentQ < QUIZ_QUESTIONS.length - 1 ? (
                  <>Next <ArrowRight className="w-4 h-4 ml-2" /></>
                ) : (
                  <>Generate Report <Sparkles className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </GlassCard>
        </div>
      </AppLayout>
    );
  }

  // ─── LOADING ────────────────────────────────────────────────────────────
  if (step === 'loading' || loading) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px]" data-testid="skills-loading">
          <LoadingSpinner />
          <p className="mt-4 text-slate-300">Generating your premium career intelligence report…</p>
          <p className="text-xs text-slate-300 mt-2">This usually takes 30-60 seconds</p>
        </div>
      </AppLayout>
    );
  }

  // ─── REPORT ─────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <PageHeader
        title="Your Skills Assessment Report"
        description="A personalized career intelligence dashboard"
      />

      {/* Header summary */}
      <GlassCard className="mb-6 bg-gradient-to-br from-blue-600/10 to-violet-600/10 border-blue-500/30" data-testid="skills-report-summary">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-400" /> Career Intelligence Summary
            </h2>
            <p className="text-slate-300 leading-relaxed">{report?.summary}</p>
          </div>
          <Button onClick={restart} variant="outline" className="border-slate-700 flex-shrink-0" data-testid="retake-quiz-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Retake Quiz
          </Button>
        </div>
      </GlassCard>

      {/* Skill Strength Overview */}
      <GlassCard className="mb-6" data-testid="skill-strength-overview">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-400" /> Skill Strength Overview
        </h3>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
          {Object.entries(report?.skill_scores || {}).map(([skill, score]) => (
            <SkillBar key={skill} label={skill} score={score} />
          ))}
        </div>
      </GlassCard>

      {/* AI Readiness Score */}
      {report?.ai_readiness && (
        <GlassCard className="mb-6 bg-gradient-to-br from-emerald-600/10 to-cyan-600/10 border-emerald-500/30" data-testid="ai-readiness-section">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0 flex flex-col items-center">
              <CircularScore score={report.ai_readiness.score} size={140} />
              <p className="text-emerald-400 font-semibold mt-2">AI Readiness Score</p>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-emerald-400" /> Your AI-Powered Future of Work
              </h3>
              <p className="text-slate-300 mb-4">{report.ai_readiness.recommendation}</p>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                {Object.entries(report.ai_readiness.breakdown || {}).map(([k, v]) => (
                  <SkillBar key={k} label={k} score={v} />
                ))}
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Top Strengths + Hidden Strengths */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <GlassCard data-testid="top-strengths">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" /> Top Strengths
          </h3>
          <div className="space-y-3">
            {(report?.top_strengths || []).map((s, i) => (
              <div key={i} className="p-3 rounded-lg bg-slate-900/40 border border-slate-700">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-medium">{s.skill}</span>
                  <span className="text-emerald-400 font-bold">{s.score}</span>
                </div>
                <p className="text-xs text-slate-300">{s.evidence}</p>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard data-testid="hidden-strengths">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" /> Hidden Strengths
          </h3>
          <ul className="space-y-2">
            {(report?.hidden_strengths || []).map((h, i) => (
              <li key={i} className="p-3 rounded-lg bg-slate-900/40 border border-slate-700">
                <span className="text-white font-medium block">{h.skill}</span>
                <span className="text-xs text-slate-300">{h.why}</span>
              </li>
            ))}
          </ul>
        </GlassCard>
      </div>

      {/* Transferable Skills */}
      <GlassCard className="mb-6" data-testid="transferable-skills">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <ArrowRight className="w-5 h-5 text-cyan-400" /> Transferable Skills
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {(report?.transferable_skills || []).map((t, i) => (
            <div key={i} className="p-4 rounded-lg bg-slate-900/40 border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-slate-300 text-sm">{t.current_skill}</span>
                <ArrowRight className="w-3 h-3 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">{t.transfers_to}</span>
              </div>
              <p className="text-xs text-slate-300 mb-2">
                Roles: {(t.potential_roles || []).join(' · ')}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-400" style={{ width: `${t.confidence}%` }} />
                </div>
                <span className="text-xs text-cyan-400 font-medium">{t.confidence}%</span>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Career Pivot Matches */}
      <GlassCard className="mb-6" data-testid="career-pivot-matches">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Compass className="w-5 h-5 text-blue-400" /> Best Career Pivot Matches
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          {(report?.career_pivot_matches || []).map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="p-4 rounded-lg bg-slate-900/40 border border-slate-700"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-semibold">{m.role}</h4>
                <span className="px-2 py-1 rounded-md bg-blue-500/20 text-blue-400 text-xs font-bold">
                  {m.match_score}% match
                </span>
              </div>
              <p className="text-xs text-slate-300 mb-3">{m.why_it_fits}</p>
              <div className="border-t border-slate-700 pt-3">
                <p className="text-xs text-amber-400 mb-1">Skills to improve:</p>
                <div className="flex flex-wrap gap-1">
                  {(m.skills_to_improve || []).map((s, j) => (
                    <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </GlassCard>

      {/* Skill Gaps */}
      <GlassCard className="mb-6" data-testid="skill-gaps">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" /> Skill Gaps
        </h3>
        <div className="space-y-3">
          {(report?.skill_gaps || []).map((g, i) => {
            const pColor = g.priority === 'High' ? 'text-red-400 bg-red-500/10 border-red-500/30' :
                           g.priority === 'Medium' ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' :
                           'text-slate-300 bg-slate-500/10 border-slate-500/30';
            return (
              <div key={i} className="p-4 rounded-lg bg-slate-900/40 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{g.skill}</span>
                  <span className={`px-2 py-0.5 rounded-md text-xs border ${pColor}`}>{g.priority}</span>
                </div>
                <p className="text-xs text-slate-300 mb-1"><strong>Why it matters:</strong> {g.why_it_matters}</p>
                <p className="text-xs text-slate-300 mb-1"><strong>Action:</strong> {g.recommended_action}</p>
                {g.suggested_resource && (
                  <p className="text-xs text-blue-400"><BookOpen className="w-3 h-3 inline mr-1" />{g.suggested_resource}</p>
                )}
              </div>
            );
          })}
        </div>
      </GlassCard>

      {/* Growth Roadmap */}
      {report?.growth_roadmap && (
        <GlassCard className="bg-gradient-to-br from-violet-600/10 to-blue-600/10 border-violet-500/30" data-testid="growth-roadmap">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-violet-400" /> Your 30-Day Growth Roadmap
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm text-violet-400 mb-2">Next 30 Days</h4>
              <ol className="space-y-2 list-decimal list-inside text-sm text-slate-300">
                {(report.growth_roadmap.next_30_days || []).map((a, i) => <li key={i}>{a}</li>)}
              </ol>
            </div>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm text-emerald-400 mb-1">Recommended Certifications</h4>
                <p className="text-xs text-slate-300">{(report.growth_roadmap.recommended_certifications || []).join(', ')}</p>
              </div>
              <div>
                <h4 className="text-sm text-blue-400 mb-1">Resume Keywords</h4>
                <div className="flex flex-wrap gap-1">
                  {(report.growth_roadmap.recommended_resume_keywords || []).map((k, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20">{k}</span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm text-cyan-400 mb-1">LinkedIn Keywords</h4>
                <div className="flex flex-wrap gap-1">
                  {(report.growth_roadmap.linkedin_profile_keywords || []).map((k, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">{k}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </GlassCard>
      )}
    </AppLayout>
  );
}
